import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../shared/store/store';
import { attributeService } from '../services/attributeService';
import { AttributeState, AttributeType } from '../types/attribute.types';

const tok = (s: RootState) => s.auth.accessToken ?? '';

export const fetchAttributes = createAsyncThunk(
    'sellerAttr/fetchAll',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await attributeService.getAttributes(params, tok(getState() as RootState));
        return data;
    }
);

export const createAttribute = createAsyncThunk(
    'sellerAttr/create',
    async (payload: { name: string; attributeType: AttributeType; colorHex?: string }, { getState }) => {
        const { data } = await attributeService.createAttribute(payload, tok(getState() as RootState));
        return data.attribute;
    }
);

export const updateAttribute = createAsyncThunk(
    'sellerAttr/update',
    async ({ id, body }: { id: string; body: Partial<{ name: string; colorHex: string; status: string }> }, { getState }) => {
        const { data } = await attributeService.updateAttribute(id, body, tok(getState() as RootState));
        return data.attribute;
    }
);

export const deleteAttribute = createAsyncThunk(
    'sellerAttr/delete',
    async (id: string, { getState }) => {
        await attributeService.deleteAttribute(id, tok(getState() as RootState));
        return id;
    }
);

export const toggleAttributeStatus = createAsyncThunk(
    'sellerAttr/toggleStatus',
    async ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }, { getState }) => {
        await attributeService.toggleStatus(id, status, tok(getState() as RootState));
        return { id, status };
    }
);

const initialState: AttributeState = {
    attributes: [],
    total     : 0,
    page      : 1,
    loading   : false,
    error     : null,
};

const attributeSlice = createSlice({
    name: 'sellerAttr',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAttributes.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchAttributes.fulfilled, (s, a) => {
                s.loading    = false;
                s.attributes = a.payload.attributes;
                s.total      = a.payload.total;
                s.page       = a.payload.page;
            })
            .addCase(fetchAttributes.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(createAttribute.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(createAttribute.fulfilled, (s, a) => { s.loading = false; s.attributes.push(a.payload); s.total += 1; })
            .addCase(createAttribute.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateAttribute.fulfilled, (s, a) => {
                if (!a.payload) return;
                const i = s.attributes.findIndex(x => x._id === a.payload._id);
                if (i !== -1) s.attributes[i] = a.payload;
            });

        builder
            .addCase(deleteAttribute.fulfilled, (s, a) => {
                s.attributes = s.attributes.filter(x => x._id !== a.payload);
                s.total -= 1;
            });

        builder
            .addCase(toggleAttributeStatus.fulfilled, (s, a) => {
                const x = s.attributes.find(x => x._id === a.payload.id);
                if (x) x.status = a.payload.status;
            });
    },
});

export default attributeSlice.reducer;
